import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react'

// [ library ]
import { nanoid } from 'nanoid'
import { useShallow } from 'zustand/shallow'

// [ hooks ]
import { useYjsService } from '../hooks/useYjsService.js'

// [ components ]
import Canvas from './Canvas'
import ToolBar from './layouts/toolpanel/ToolBar'
import TopPanel from './layouts/TopPanel'
import BottomPanel from './layouts/BottomPanel'
import Loading from './overlays/Loading'
import ImageLock from './overlays/ImageLock'
import {
  getBoundaryPoints,
  getCanvasCoord,
  getImageInitialData,
  subtractCoordinates,
} from '../utils/utils'
import { updateHistoryStackImage } from '../utils/undoRedoUtils'

// [ store ]
import { useUIStore } from '../stores/uiStore'

// [ config ]
const cursorByTool = {
  select: 'default',
  move: 'grabbing',
  'pen-pressure': 'crosshair',
  pen: 'crosshair',
  'pen-highlight': 'crosshair',
  eraser: 'crosshair',
  shape: 'crosshair',
}

const opacityVal = {
  'pen-pressure': 1,
  pen: 1,
  'pen-highlight': 0.5,
  shape: 1,
}

const WhiteBoard = forwardRef((props, ref) => {
  // uiStore
  const { isLoading, setLoading } = useUIStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      setLoading: state.setLaoding,
    }))
  )

  /**
   * --------------------------------------------
   *          [ 상태 관리 및 데이터 초기화 ]
   * --------------------------------------------
   */
  const [isPenMode, setIsPenMode] = useState(false)
  const [isStylePanelVisible, setIsStylePanelVisible] = useState(true)
  const [isMoreButtonVisible, setIsMoreButtonVisible] = useState(false)
  const [isSimulatePressure, setIsSimulatePressure] = useState(false)
  const [isShapeFilled, setIsShapeFilled] = useState(false)

  const [paths, setPaths] = useState({})
  // const [isLoading, setIsLoading] = useState(true)
  const [isLeader, setIsLeader] = useState(props.isLeader)

  const [scope, setScope] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [selectedTool, setSelectedTool] = useState('pen')
  const [selectedShape, setSelectedShape] = useState('square')
  const [selectionRect, setSelectionRect] = useState({ top: 0, left: 0, width: 0, height: 0 })
  const [selectionRectPathKeys, setSelectionRectPathKeys] = useState([])
  const [lockedCheckKey, setLockedCheckKey] = useState('')
  // 동시편집 undo/redo 관리 stack
  const [historyStack, setHistoryStack] = useState({})

  const [colorVal, setColorVal] = useState({
    'pen-pressure': '#FF4242',
    pen: '#000000',
    'pen-highlight': '#FFC06E',
    shape: '#000000',
  })
  const [thicknessVal, setThicknessVal] = useState({
    'pen-pressure': 4,
    pen: 4,
    'pen-highlight': 30,
    shape: 4,
  })

  const scaleRatio = useRef()
  const getBoundingClientSgCanvas = useRef({ x: 0, y: 0 })

  // ============================================================================
  // ============================================================================
  /**
   * --------------------------------------------
   *                [ 동시편집 ]
   * --------------------------------------------
   */
  const [boardId, setBoardId] = useState(props.boardId)
  const [boardToken, setBoardToken] = useState('')

  // props 변경에 따른 boardId / token 동기화
  useEffect(() => {
    if (boardId !== props.boardId) setBoardId(props.boardId)
    if (boardToken !== props.token) setBoardToken(props.token)

    const handleVisibilityChange = () => {
      console.log(document.visibilityState)
      if (document.visibilityState === 'visible') {
        console.log(document.visibilityState)
        // 우선 임시 새로고침
        // window.location.reload()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)

    // eslint-disable-next-line
  }, [props.boardId, props.token])

  useEffect(() => {
    // 1. undo/redo 스택 초기화 (boardId 단위)
    setHistoryStack((prev) => {
      // 기존 history에 해당 `boardId`가 존재하면 그대로 반환 (중복 초기화 방지)
      if (prev[boardId]) return prev
      // 새로운 `boardId`에 대한 히스토리 스택을 초기화
      return {
        ...prev,
        [boardId]: { stack: [], pointer: -1 },
      }
    })

    // 2. 선택 초기화
    setSelectionRect({ top: 0, left: 0, width: 0, height: 0 })
    setSelectionRectPathKeys([])
    setLockedCheckKey('')
  }, [boardId, setHistoryStack])

  // 1. Yjs 데이터 변경을 처리할 콜백 함수들을 useCallback으로 정의
  const handlePathsUpdate = useCallback(
    (newPaths) => {
      setPaths(newPaths)
    },
    [setPaths]
  )

  const handleScopeTranslateUpdate = useCallback(
    (scopeEntries, translateEntries) => {
      // [SCOPE]
      const incomingScope = (scopeEntries['scope'] || 1) * scaleRatio.current
      setScope((prev) => (prev !== incomingScope ? incomingScope : prev))

      // [TRANSLATE]
      const incomingTranslate = {
        x: translateEntries['translateX'] || 0,
        y: translateEntries['translateY'] || 0,
      }
      setTranslate((prev) =>
        prev.x !== incomingTranslate.x || prev.y !== incomingTranslate.y ? incomingTranslate : prev
      )
    },
    [scaleRatio]
  )

  // 2. Yjs 연결 훅 호출
  const {
    providerRef,
    ydocRef,
    yPathsRef,
    yScopeRef,
    yTranslateRef,
    triggerAddPath,
    triggerUpdatePath,
    triggerUpdateTranslateAndScope,
  } = useYjsService({
    wsUrl: props.wsUrl,
    boardId: props.boardId,
    boardToken: props.token,
    isListener: props.isListener,
    isLeader: props.isLeader,
    scaleRatio,
    onPathsUpdate: handlePathsUpdate,
    onScopeTranslateUpdate: handleScopeTranslateUpdate,
  })

  // ============================================================================
  // 동시편집 - [image]
  // ============================================================================
  const [imageUrls, setImageUrls] = useState({})

  // 서버에서 이미지 url 받아오기
  const getImageUrl = async (src) => {
    if (!src) return

    const response = await fetch('https://api.klassmate.io/classrooms/background', {
      method: 'POST',
      body: JSON.stringify({
        src: src,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
    const res = await response.json()
    if (res.result === 'ERROR') return

    return res.data.imageUrl
  }

  useEffect(() => {
    const getImages = async () => {
      for await (const [, value] of Object.entries(paths)) {
        if (value.type === 'image') {
          if (!(value.src in imageUrls)) {
            let url = await getImageUrl(value.src)
            imageUrls[value.src] = url
          }
        }
      }

      setImageUrls({ ...imageUrls })
    }

    getImages()
    // eslint-disable-next-line
  }, [paths])

  const getImageDataAndInitializePaths = (img) => {
    // 화면 중앙 좌표 계산
    const canvasCenterCoord = subtractCoordinates(
      getCanvasCoord({ x: window.innerWidth / 2, y: window.innerHeight / 2 }, translate, scope),
      getBoundingClientSgCanvas.current
    )

    const id = nanoid() // 고유 ID 생성
    const newImage = getImageInitialData(
      'image',
      Object.keys(paths).length,
      canvasCenterCoord.x - img.w / 2,
      canvasCenterCoord.y - img.h / 2,
      0,
      0,
      img.w,
      img.h,
      img.w,
      img.h,
      1,
      img.src,
      1,
      1,
      img.h / img.w,
      false
    )
    getBoundaryPoints('image', newImage)
    triggerAddPath(id, newImage)

    setLockedCheckKey(id)
    setSelectionRectPathKeys([id])
    setSelectedTool('select')
    setIsStylePanelVisible(false)

    updateHistoryStackImage({ boardId, id, newImage, historyStack, setHistoryStack })
  }

  useImperativeHandle(ref, () => ({
    getImageDataAndInitializePaths,
  }))

  // ============================================================================
  // ============================================================================

  return (
    <div
      id='sg-canvas-container'
      className='sg-canvas-container'
      draggable={false}
      style={{
        cursor: props.isWritable ? cursorByTool[selectedTool] : 'default',
      }}
    >
      {/* sg-canvas-container > sg-canvas */}
      <Canvas
        boardId={boardId}
        isWritable={props.isWritable}
        isiOSAfterImageStyleActive={props.isiOSAfterImageStyleActive}
        /////////////////////////////
        paths={paths}
        setPaths={setPaths}
        yPathsRef={yPathsRef}
        triggerAddPath={triggerAddPath}
        triggerUpdatePath={triggerUpdatePath}
        triggerUpdateTranslateAndScope={triggerUpdateTranslateAndScope}
        ydocRef={ydocRef}
        yScopeRef={yScopeRef}
        yTranslateRef={yTranslateRef}
        // useState
        isPenMode={isPenMode}
        setIsPenMode={setIsPenMode}
        isStylePanelVisible={isStylePanelVisible}
        setIsStylePanelVisible={setIsStylePanelVisible}
        isMoreButtonVisible={isMoreButtonVisible}
        setIsMoreButtonVisible={setIsMoreButtonVisible}
        isShapeFilled={isShapeFilled}
        isSimulatePressure={isSimulatePressure}
        setIsSimulatePressure={setIsSimulatePressure}
        scope={scope}
        setScope={setScope}
        translate={translate}
        setTranslate={setTranslate}
        selectedTool={selectedTool}
        setSelectedTool={setSelectedTool}
        selectedShape={selectedShape}
        colorVal={colorVal}
        thicknessVal={thicknessVal}
        opacityVal={opacityVal}
        selectionRect={selectionRect}
        setSelectionRect={setSelectionRect}
        selectionRectPathKeys={selectionRectPathKeys}
        setSelectionRectPathKeys={setSelectionRectPathKeys}
        lockedCheckKey={lockedCheckKey}
        setLockedCheckKey={setLockedCheckKey}
        historyStack={historyStack}
        setHistoryStack={setHistoryStack}
        imageUrls={imageUrls}
        // useRef
        scaleRatio={scaleRatio}
        getBoundingClientSgCanvas={getBoundingClientSgCanvas}
      />

      {/* sg-canvas-container > sg-layout */}
      <div id='sg-layout'>
        {providerRef.current && providerRef.current.room && providerRef.current.room.peerId && (
          <p className='sg-ml-60'>{providerRef.current.room.peerId}</p>
        )}
        <TopPanel
          isDev={props.isDev}
          ///////////////////
          isPenMode={isPenMode}
          setIsPenMode={setIsPenMode}
          isLeader={isLeader}
          setIsLeader={setIsLeader}
        />
        <ToolBar
          boardId={boardId}
          isStudent={props.isStudent}
          isWritable={props.isWritable}
          // [path]
          paths={paths}
          setPaths={setPaths}
          // [state]
          setIsPenMode={setIsPenMode}
          selectedTool={selectedTool}
          setSelectedTool={setSelectedTool}
          selectionRect={selectionRect}
          setSelectionRect={setSelectionRect}
          selectionRectPathKeys={selectionRectPathKeys}
          setSelectionRectPathKeys={setSelectionRectPathKeys}
          lockedCheckKey={lockedCheckKey}
          historyStack={historyStack}
          setHistoryStack={setHistoryStack}
          // <stylePanel>
          isStylePanelVisible={isStylePanelVisible}
          setIsStylePanelVisible={setIsStylePanelVisible}
          colorVal={colorVal}
          setColorVal={setColorVal}
          thicknessVal={thicknessVal}
          setThicknessVal={setThicknessVal}
          selectedShape={selectedShape}
          setSelectedShape={setSelectedShape}
          isShapeFilled={isShapeFilled}
          setIsShapeFilled={setIsShapeFilled}
          // <imageUpload>
          scope={scope}
          translate={translate}
          setLockedCheckKey={setLockedCheckKey}
          getBoundingClientSgCanvas={getBoundingClientSgCanvas}
          uploadImage={props.uploadImage}
          openImageModal={props.openImageModal}
          getImageDataAndInitializePaths={getImageDataAndInitializePaths}
          // 동시편집
          ydocRef={ydocRef}
          yPathsRef={yPathsRef}
          triggerAddPath={triggerAddPath}
          triggerUpdatePath={triggerUpdatePath}
        />
        <BottomPanel
          boardId={boardId}
          isWritable={props.isWritable}
          // paths
          paths={paths}
          setPaths={setPaths}
          // scope && translate
          scope={scope}
          setScope={setScope}
          yScopeRef={yScopeRef}
          yTranslateRef={yTranslateRef}
          translate={translate}
          setTranslate={setTranslate}
          scaleRatio={scaleRatio}
          historyStack={historyStack}
          setHistoryStack={setHistoryStack}
          selectionRectPathKeys={selectionRectPathKeys}
          setSelectionRectPathKeys={setSelectionRectPathKeys}
          // <more button>
          isMoreButtonVisible={isMoreButtonVisible}
          setIsMoreButtonVisible={setIsMoreButtonVisible}
          lockedCheckKey={lockedCheckKey}
          setLockedCheckKey={setLockedCheckKey}
          // 동시편집
          ydocRef={ydocRef}
          yPathsRef={yPathsRef}
          triggerAddPath={triggerAddPath}
          triggerUpdatePath={triggerUpdatePath}
          triggerUpdateTranslateAndScope={triggerUpdateTranslateAndScope}
        />

        {lockedCheckKey && (
          <ImageLock
            isWritable={props.isWritable}
            paths={paths}
            setPaths={setPaths}
            yPathsRef={yPathsRef}
            lockedCheckKey={lockedCheckKey}
            setSelectionRectPathKeys={setSelectionRectPathKeys}
            // 동시편집
            triggerUpdatePath={triggerUpdatePath}
          />
        )}
      </div>
      <Loading isLoading={isLoading} setIsLoading={setLoading} />
    </div>
  )
})

export default WhiteBoard
